import { Outlet, redirect, useLocation, useRouter } from '@tanstack/react-router';
import { PageLoader } from '@customerio/pluma-components/react';
import Journeys from '@/pages/journeys';
import { PlanGuard } from '@/components/guards';
import { BUILDER_PLAN_SLUG } from '@/services/auth/plan-slugs';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { ProtectedRoute } from '@/services/router/components/protected-route';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { generatePath } from '@/services/router/generate-path';
import type { WorkspaceIdRouteParams } from '@/services/router/workspace-id-param';
import { getWorkspaceIdFromUrl } from '@/utils/workspace-id';
import { IntegrationsShell, type IntegrationsTab } from './integrations-shell';

// The migrated routes drop the `journeys` segment: Ember serves `/journeys/integrations/...`, the
// Hydra port lives at `/integrations/...`. The old URLs stay registered as flag-gated redirects
// (see `JourneysIntegrationsHandoff`) so Ember-side links keep working without every caller
// being re-pointed.
const INTEGRATIONS_OVERVIEW_PATH = '/workspaces/$workspaceId/integrations/all/overview';
const INTEGRATIONS_DIRECTORY_PATH = '/workspaces/$workspaceId/integrations/all/directory';

/**
 * Both index routes (`/integrations` and `/integrations/all`) redirect straight to the Connections
 * tab at `/integrations/all/overview` — a single hop to a concrete leaf, never through the
 * intermediate `/integrations/all`. Ember lands the user at `…/all/overview` too, so the skipped
 * hop is not observable; going direct avoids a chained beforeLoad redirect (index → `/all`, then
 * `/all`'s index → `/all/overview`) that stalled blank on a cold load. Static (no flag read) so it
 * can't race the account load, and the target is a URL the embedded app also recognizes, so it's
 * correct in both flag states. `search: true` carries any query string through the redirect
 * (Ember transitions preserve query params).
 */
export function redirectToIntegrationsOverview(params: WorkspaceIdRouteParams): void {
  throw redirect({
    to: INTEGRATIONS_OVERVIEW_PATH,
    params: { workspaceId: params.workspaceId ?? 'last' },
    search: true,
  });
}

/** Where a `journeys` URL hands off to, once the flag is on. */
export type IntegrationsHandoffTarget = 'overview' | 'directory';

/**
 * The old `/journeys/integrations/...` URLs. Flag off → the embedded Ember app still owns the
 * page and renders here. Flag on → Hydra has taken the URL over purely to hand it on to the
 * migrated route, which lives at the same path minus the `journeys` segment.
 *
 * This is what lets the Ember app keep linking to its own URLs after the migration: the links do
 * not need re-pointing, they land here and get forwarded. In-Hydra links target the new URL
 * directly, so they never pay for this hop.
 */
type JourneysIntegrationsHandoffProps = {
  target: IntegrationsHandoffTarget;
};

export function JourneysIntegrationsHandoff({ target }: JourneysIntegrationsHandoffProps) {
  const isHydraIntegrations = useFeatureFlag(AccountFeatureFlag.HydraIntegrationsDirectory);
  const { pathname, searchStr, hash } = useLocation();

  if (!isHydraIntegrations) {
    return <Journeys />;
  }

  // Same reason as the shell: `/workspaces/last` is rewritten by a bridge-synced navigation that
  // leaves route params stale, so read the id off the reactive location and hold until it lands.
  const workspaceId = getWorkspaceIdFromUrl(pathname);
  if (workspaceId == null) {
    return <PageLoader label="Loading integrations…" />;
  }

  const to = generatePath(
    target === 'directory'
      ? '/workspaces/:workspaceId/integrations/all/directory'
      : '/workspaces/:workspaceId/integrations/all/overview',
    { workspaceId },
  );

  // Carry the query string and hash across: Ember transitions preserve them, and the directory
  // deep-links (`?selectedType=`, `?selectedCategory=`) are exactly what arrives on these URLs.
  return (
    <SafeNavigate
      to={`${to}${searchStr}${hash === '' ? '' : `#${hash}`}`}
      options={{ shouldReplaceHistoryEntry: true }}
    />
  );
}

type IntegrationsShellWithRouterProps = {
  workspaceId: string;
};

function IntegrationsShellWithRouter({ workspaceId }: IntegrationsShellWithRouterProps) {
  const router = useRouter();
  const { pathname } = useLocation();
  // Route ownership claims `…/all/directory/` too, so trim the trailing slash before matching —
  // Ember reads `router.currentRouteName` here, which a trailing slash cannot change.
  const activeTab: IntegrationsTab = pathname.replace(/\/+$/, '').endsWith('/directory') ? 'directory' : 'connections';
  const overviewHref = router.buildLocation({ to: INTEGRATIONS_OVERVIEW_PATH, params: { workspaceId } }).href;
  const directoryHref = router.buildLocation({ to: INTEGRATIONS_DIRECTORY_PATH, params: { workspaceId } }).href;

  return (
    <IntegrationsShell
      workspaceId={workspaceId}
      activeTab={activeTab}
      overviewHref={overviewHref}
      directoryHref={directoryHref}
    >
      <Outlet />
    </IntegrationsShell>
  );
}

/**
 * The `/integrations/all` shell layout — the migrated URL, without the `journeys` segment.
 *
 * Flag on → Hydra owns the page, applying the same route-access gates the Ember root route did:
 * Builder-plan accounts go to the available-plans page, and accounts missing
 * `journeys/imports/view` are redirected away with an access-denied alert.
 *
 * Flag off → this URL has no Ember counterpart (Ember only knows the `journeys` form), so
 * rendering the embedded host here would 404 inside Ember. Hand back to the `journeys` twin
 * instead, which Ember does serve — the exact inverse of `JourneysIntegrationsHandoff`.
 */
export function IntegrationsAllRouteView() {
  const isHydraIntegrations = useFeatureFlag(AccountFeatureFlag.HydraIntegrationsDirectory);
  const { pathname, searchStr, hash } = useLocation();
  const resolvedWorkspaceId = getWorkspaceIdFromUrl(pathname);

  if (!isHydraIntegrations) {
    if (resolvedWorkspaceId == null) {
      return <PageLoader label="Loading integrations…" />;
    }
    const emberHref = generatePath('/workspaces/:workspaceId/journeys/integrations/all/overview', {
      workspaceId: resolvedWorkspaceId,
    });
    return (
      <SafeNavigate
        to={`${emberHref}${searchStr}${hash === '' ? '' : `#${hash}`}`}
        options={{ shouldReplaceHistoryEntry: true }}
      />
    );
  }

  // Derive the workspace id from the (reactive) router location, not route params: LastWorkspaceResolver
  // rewrites the `/workspaces/last` sentinel with a bridge-synced navigation that does not reload the
  // route, so route params stay stale at `last` (CON-7180). Hold on a loader until the id is a real
  // number. The shell reads its workspace-scoped data with this resolved id (never the raw URL), so it
  // does not depend on window.location catching up to the router location.
  const workspaceId = getWorkspaceIdFromUrl(pathname);
  if (workspaceId == null) {
    return <PageLoader label="Loading integrations…" />;
  }

  return (
    <PlanGuard excludedPlans={[BUILDER_PLAN_SLUG]}>
      <ProtectedRoute
        permission="journeys/imports/view"
        redirectTo={generatePath('/workspaces/:workspaceId', { workspaceId })}
      >
        <IntegrationsShellWithRouter workspaceId={workspaceId} />
      </ProtectedRoute>
    </PlanGuard>
  );
}
